import { useEffect } from 'react';
import {
  BrowserRouter,
  Navigate,
  Outlet,
  Route,
  Routes,
  useLocation,
  useParams,
} from 'react-router';
import { useTranslation } from 'react-i18next';
import { SUPPORTED_LOCALES, type Locale, isSupportedLocale } from '@boletra/i18n';
import { LandingPage } from './features/landing/LandingPage.js';
import { LoginPage } from './features/auth/LoginPage.js';
import { SignupPage } from './features/auth/SignupPage.js';
import { VerifyMagicLinkPage } from './features/auth/VerifyMagicLinkPage.js';
import { DashboardHomePage } from './features/dashboard/DashboardHomePage.js';
import { CreditsPage } from './features/dashboard/CreditsPage.js';
import { EventListPage } from './features/dashboard/EventListPage.js';
import { CreateEventPage } from './features/dashboard/CreateEventPage.js';
import { EditEventPage } from './features/dashboard/EditEventPage.js';
import { EventSalesPage } from './features/dashboard/EventSalesPage.js';
import { SettingsPage } from './features/dashboard/SettingsPage.js';
import { OnboardingPage } from './features/dashboard/OnboardingPage.js';
import { AdminPage } from './features/dashboard/AdminPage.js';
import { DiscoverPage } from './features/discover/DiscoverPage.js';
import { PublicEventPage } from './features/event/PublicEventPage.js';
import { CheckoutPage } from './features/checkout/CheckoutPage.js';
import { OrderConfirmationPage } from './features/checkout/OrderConfirmationPage.js';
import { TicketViewerPage } from './features/ticket/TicketViewerPage.js';
import { ScanPage } from './features/scan/ScanPage.js';
import { NotFoundPage } from './features/NotFoundPage.js';
import { PrivacyPage } from './features/legal/PrivacyPage.js';
import { TermsPage } from './features/legal/TermsPage.js';
import { RequireAuth } from './components/RequireAuth.js';
import { useAuthStore } from './lib/auth.js';
import { trackPageView } from './lib/analytics.js';

function LocaleGuard() {
  const { lang } = useParams();
  const { i18n } = useTranslation();
  useEffect(() => {
    if (lang && isSupportedLocale(lang) && i18n.language !== lang) {
      i18n.changeLanguage(lang);
      document.documentElement.lang = lang;
    }
  }, [lang, i18n]);
  if (!lang || !isSupportedLocale(lang)) {
    return <Navigate to={`/${(i18n.language as Locale) ?? 'es'}`} replace />;
  }
  return null;
}

export function App() {
  const hydrated = useAuthStore((s) => s.hydrated);
  const hydrate = useAuthStore((s) => s.hydrate);

  useEffect(() => {
    void hydrate();
  }, [hydrate]);

  if (!hydrated) {
    return (
      <div className="min-h-screen flex items-center justify-center text-neutral-500">Loading…</div>
    );
  }

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<RedirectToDefaultLocale />} />
        <Route path="/:lang" element={<LocaleLayout />}>
          <Route index element={<LandingPage />} />
          <Route path="discover" element={<DiscoverPage />} />
          <Route path="auth/login" element={<LoginPage />} />
          <Route path="auth/signup" element={<SignupPage />} />
          <Route path="auth/verify" element={<VerifyMagicLinkPage />} />
          <Route
            path="dashboard"
            element={
              <RequireAuth>
                <DashboardHomePage />
              </RequireAuth>
            }
          />
          <Route
            path="dashboard/events"
            element={
              <RequireAuth>
                <EventListPage />
              </RequireAuth>
            }
          />
          <Route
            path="dashboard/events/new"
            element={
              <RequireAuth>
                <CreateEventPage />
              </RequireAuth>
            }
          />
          <Route
            path="dashboard/events/:id"
            element={
              <RequireAuth>
                <EditEventPage />
              </RequireAuth>
            }
          />
          <Route
            path="dashboard/events/:id/sales"
            element={
              <RequireAuth>
                <EventSalesPage />
              </RequireAuth>
            }
          />
          <Route
            path="dashboard/credits"
            element={
              <RequireAuth>
                <CreditsPage />
              </RequireAuth>
            }
          />
          <Route
            path="dashboard/settings"
            element={
              <RequireAuth>
                <SettingsPage />
              </RequireAuth>
            }
          />
          <Route
            path="dashboard/onboarding"
            element={
              <RequireAuth>
                <OnboardingPage />
              </RequireAuth>
            }
          />
          <Route
            path="dashboard/admin/*"
            element={
              <RequireAuth>
                <AdminPage />
              </RequireAuth>
            }
          />
          <Route path="legal/privacy" element={<PrivacyPage />} />
          <Route path="legal/terms" element={<TermsPage />} />
          <Route path="o/:orgSlug/:eventSlug" element={<PublicEventPage />} />
          <Route path="o/:orgSlug/:eventSlug/checkout" element={<CheckoutPage />} />
          <Route path="orders/:orderId" element={<OrderConfirmationPage />} />
          <Route path="t/:qrId" element={<TicketViewerPage />} />
          <Route path="scan/:eventId" element={<ScanPage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Route>
        <Route path="*" element={<RedirectToDefaultLocale />} />
      </Routes>
    </BrowserRouter>
  );
}

function RedirectToDefaultLocale() {
  const { i18n } = useTranslation();
  const target = SUPPORTED_LOCALES.includes(i18n.language as Locale)
    ? (i18n.language as Locale)
    : 'es';
  return <Navigate to={`/${target}`} replace />;
}

function LocaleLayout() {
  return (
    <>
      <LocaleGuard />
      <ScrollManager />
      <div className="min-h-screen flex flex-col">
        <main className="flex-1">
          <Outlet />
        </main>
      </div>
    </>
  );
}

/**
 * Handles scroll behaviour across navigations:
 *   - On `#hash` change, smooth-scrolls the section into view (with sticky-
 *     header offset). This is what makes the "Pricing" / "How it works"
 *     header links work when we're already on the landing page — react-router
 *     <Link> updates the URL but does NOT scroll on its own.
 *   - On a full path change (no hash), resets to top.
 */
function ScrollManager() {
  const { pathname, hash, search, key } = useLocation();
  useEffect(() => {
    if (hash) {
      // Defer one tick so the target section is in the DOM (e.g. when the
      // landing page is mounting for the first time on a deep-link).
      const id = hash.startsWith('#') ? hash.slice(1) : hash;
      requestAnimationFrame(() => {
        const el = document.getElementById(id);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
      return;
    }
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' as ScrollBehavior });
  }, [pathname, hash, key]);

  // GA4 page_view on every SPA navigation. Defer one tick so document.title
  // has been updated by <Seo> for the new page before we send the event.
  useEffect(() => {
    const fullPath = `${pathname}${search}${hash}`;
    const t = setTimeout(() => trackPageView(fullPath), 0);
    return () => clearTimeout(t);
  }, [pathname, search, hash]);

  return null;
}
